/**
 * Server-Sent Events Service
 *
 * Manages EventSource connection to backend SSE endpoint.
 * Handles reconnection, authentication, and event routing.
 *
 * WP-RT-2: Frontend Real-Time Integration
 */

import { getAccessToken } from '@/services/api';
import { config } from '@/config';

const SSE_ENDPOINT = '/v1/events/stream';

// Reconnection settings
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30000; // 30 seconds
const RETRY_MULTIPLIER = 2;

// Event types that can be received from SSE
// MUST match SSE_FORWARDED_EVENTS in WP-RT-1 backend
export type SSEEventType =
  | 'connected' // Pseudo-event for connection state
  // Sync lifecycle events
  | 'sync.started'
  | 'sync.completed'
  | 'sync.failed'
  | 'holdings.synced' // Investment account sync completed
  // Account events
  | 'account.disconnected'
  | 'balance.mismatch' // Balance reconciliation mismatch detected
  // Connection events
  | 'connection.deleted' // User removed a connection
  // Notification events
  | 'notification.created'; // New notification (triggers badge update)

export interface SSEEvent {
  eventType: SSEEventType;
  timestamp: string;
  correlationId: string;
  payload: Record<string, unknown>;
}

export type SSEEventHandler = (event: SSEEvent) => void;

export interface EventSourceOptions {
  onEvent: SSEEventHandler;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

/**
 * Create an authenticated EventSource connection.
 *
 * Note: Native EventSource doesn't support custom headers.
 * We use query params for auth (backend accepts both).
 * This is standard practice for SSE with JWT.
 */
export function createEventSource(options: EventSourceOptions): {
  close: () => void;
  isConnected: () => boolean;
} {
  let eventSource: EventSource | null = null;
  let retryDelay = INITIAL_RETRY_DELAY;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let connected = false;
  let lastEventId: string | null = null;
  let isClosed = false;

  const connect = () => {
    if (isClosed) return;

    const token = getAccessToken();
    if (!token) {
      console.warn('[SSE] No auth token, skipping connection');
      return;
    }

    // Build URL with auth token as query param
    // (EventSource doesn't support Authorization header)
    // CRITICAL: Use config.apiUrl for Docker runtime config support
    const url = new URL(SSE_ENDPOINT, config.apiUrl);
    url.searchParams.set('token', token);

    // Add last event ID for reconnection
    if (lastEventId) {
      url.searchParams.set('last_event_id', lastEventId);
    }

    eventSource = new EventSource(url.toString());

    eventSource.onopen = () => {
      connected = true;
      retryDelay = INITIAL_RETRY_DELAY; // Reset on successful connect
      options.onConnect?.();
      console.log('[SSE] Connected');
    };

    eventSource.onerror = (error) => {
      connected = false;
      options.onDisconnect?.();
      options.onError?.(error);

      // Close and schedule reconnect
      eventSource?.close();
      scheduleReconnect();
    };

    // Listen for all event types (must match SSEEventType)
    const eventTypes: SSEEventType[] = [
      'connected',
      'sync.started',
      'sync.completed',
      'sync.failed',
      'holdings.synced',
      'account.disconnected',
      'balance.mismatch',
      'connection.deleted',
      'notification.created',
    ];

    eventTypes.forEach((eventType) => {
      eventSource?.addEventListener(eventType, (e: MessageEvent) => {
        try {
          // Store last event ID for reconnection
          if ((e as MessageEvent & { lastEventId?: string }).lastEventId) {
            lastEventId = (e as MessageEvent & { lastEventId: string }).lastEventId;
          }

          const data = JSON.parse(e.data);
          options.onEvent({
            eventType,
            timestamp: data.timestamp,
            correlationId: data.correlationId,
            payload: data.payload,
          });
        } catch (err) {
          console.error('[SSE] Failed to parse event', err);
        }
      });
    });
  };

  const scheduleReconnect = () => {
    if (retryTimeout || isClosed) return;

    console.log(`[SSE] Reconnecting in ${retryDelay}ms`);
    retryTimeout = setTimeout(() => {
      retryTimeout = null;
      retryDelay = Math.min(retryDelay * RETRY_MULTIPLIER, MAX_RETRY_DELAY);
      connect();
    }, retryDelay);
  };

  const close = () => {
    isClosed = true;
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
    eventSource?.close();
    eventSource = null;
    connected = false;
  };

  // Initial connection
  connect();

  return {
    close,
    isConnected: () => connected,
  };
}
